import { useState, useEffect, useContext } from 'react';
import {
  Select,
  TextInput,
  Stack,
  Text,
  Modal,
  Button,
  Group
} from '@mantine/core';
import { ChatContext } from '../context/ChatContext';
import { useMantineTheme } from '@mantine/core';

const providers = [
    { value: 'hidemium', label: 'Hidemium Free' },
    { value: 'anthropic', label: 'Anthropic' },
    { value: 'gemini', label: 'Gemini' },
    { value: 'openai', label: 'OpenAI' },
];

// ===== CLAUDE (Anthropic) =====
// Source: https://platform.claude.com/docs/en/about-claude/models/overview
const claudeModels = [
  { value: 'claude-opus-4-7',            label: 'Claude Opus 4.7 (Most capable)' },
  { value: 'claude-sonnet-4-6',          label: 'Claude Sonnet 4.6 (Best speed/intelligence)' },
  { value: 'claude-haiku-4-5-20251001',  label: 'Claude Haiku 4.5 (Fastest)' },

  // --- Legacy (still available, not recommended for new projects) ---
  { value: 'claude-sonnet-4-20250514',   label: 'Claude Sonnet 4 (Legacy)' },
  { value: 'claude-opus-4-20250514',     label: 'Claude Opus 4 (Legacy)' },
];

// ===== GEMINI (Google) =====
// Source: https://ai.google.dev/gemini-api/docs/models
const geminiModels = [
  // --- Preview (production-ready) ---
  { value: 'gemini-3.1-pro-preview',      label: 'Gemini 3.1 Pro Preview' },
  { value: 'gemini-3.1-flash-lite-preview', label: 'Gemini 3.1 Flash-Lite Preview' },

  // --- Current (stable, recommended) ---
  { value: 'gemini-2.5-pro',       label: 'Gemini 2.5 Pro (Most capable, reasoning)' },
  { value: 'gemini-2.5-flash',     label: 'Gemini 2.5 Flash (Best price/performance)' },
  { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash-Lite (Fastest, lowest cost)' },
];

// ===== OPENAI =====
// Source: https://platform.openai.com/docs/models & changelog
const openaiModels = [
  // --- Current flagship (GPT-5 family) ---
  { value: 'gpt-5.5',        label: 'GPT-5.5 (Flagship, complex reasoning)' },
  { value: 'gpt-5.4',        label: 'GPT-5.4' },
  { value: 'gpt-5.4-mini',   label: 'GPT-5.4 Mini (Balanced)' },
  { value: 'gpt-5.4-nano',   label: 'GPT-5.4 Nano (Fast, low cost)' },
  { value: 'o4-mini',        label: 'OpenAI o4-mini (Fast reasoning)' },

  // --- GPT-4.1 family (still available via API) ---
  { value: 'gpt-4.1',        label: 'GPT-4.1 (Coding, 1M context)' },
  { value: 'gpt-4.1-mini',   label: 'GPT-4.1 Mini' },

  // --- GPT-4o (still in API, being phased out in ChatGPT UI) ---
  { value: 'gpt-4o',         label: 'GPT-4o (Legacy multimodal)' },

  // NOTE: gpt-4, gpt-4-turbo, gpt-3.5-turbo, gpt-3.5-turbo-16k đã deprecated/retired
];

const ConfigModel = () => {
  const { modelConfig, updateModelConfig } = useContext(ChatContext);
  const [provider, setProvider] = useState(modelConfig.provider || 'hidemium');
  const [model, setModel] = useState(modelConfig.model || '');
  const [apiKey, setApiKey] = useState(modelConfig.apiKey || '');
  const [opened, setOpened] = useState(false);
  const theme = useMantineTheme();
  const isTet = theme.colorScheme === 'tet';

  const getModelOptions = () => {
    switch (provider) {
      case 'gemini':
        return geminiModels;
      case 'openai':
        return openaiModels;
      case 'anthropic':
        return claudeModels;
      default:
        return [];
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setProvider(modelConfig.provider || 'hidemium');
      setModel(modelConfig.model || '');
      setApiKey(modelConfig.apiKey || '');
    }, [])
  }, [modelConfig, opened]);

  useEffect(() => {
    if (opened) {
      if (provider === 'gemini') {
        setModel('gemini-2.5-pro-exp-03-25');
      } else if (provider === 'openai') {
        setModel('gpt-4');
      } else if (provider === 'anthropic') {
        setModel('claude-3-7-sonnet-20250219');
      } else {
        setModel('');
      }
    }
  }, [provider, opened]);

  // Sync local state with context when modal opens
  useEffect(() => {
    if (opened) {
      setProvider(modelConfig.provider || 'hidemium');
      setModel(modelConfig.model || '');
      setApiKey(modelConfig.apiKey || '');
    }
  }, [opened, modelConfig]);

  const handleSave = () => {
    if (provider !== 'hidemium' && (!model || !apiKey)) {
      return; // Don't save if required fields are missing
    }

    // Save to context
    updateModelConfig({
      provider,
      model: provider === 'hidemium' ? null : model,
      apiKey: provider === 'hidemium' ? null : apiKey
    });

    // Close modal
    setOpened(false);
  };

  return (
    <>
      <Button
        variant="gradient"

        gradient={
          isTet
            ? { from: '#d32f2f', to: '#fbc02d', deg: 90 }
            : { from: 'indigo', to: 'cyan' }
        }

        size="xs"
        onClick={() => setOpened(true)}
      >
        Config Model
      </Button>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title="Model Configuration"
        size="md"
        zIndex={9999}
      >
        <Stack spacing="md">
          <Select
            label="Provider"
            placeholder="Select provider"
            data={providers}
            value={provider}
            onChange={setProvider}
            required
          />

          {provider !== 'hidemium' && (
            <>
              <Select
                label="Model"
                placeholder="Select model"
                data={getModelOptions()}
                value={model}
                onChange={setModel}
                required
              />

              <TextInput
                label="API Key"
                placeholder="Enter your API key"
                value={apiKey}
                onChange={(event) => setApiKey(event.currentTarget.value)}
                onKeyDown={(event) => event.stopPropagation()}
                required
              />

              {provider === 'gemini' && (
                <Text size="xs" color="dimmed">
                  <a href="https://aistudio.google.com/apikey" target='_blank'>
                    You can get your Gemini API key from Google AI Studio.
                  </a>
                </Text>
              )}

              {provider === 'openai' && (
                <Text size="xs" color="dimmed">
                  <a href="https://platform.openai.com/api-keys" target='_blank'>
                    You can get your OpenAI API key from your OpenAI account.
                  </a>
                </Text>
              )}

              {provider === 'anthropic' && (
                <Text size="xs" color="dimmed">
                  <a target='_blank' href="https://console.anthropic.com/settings/keys">
                    You can get your Anthropic API key from your Anthropic account.
                  </a>
                </Text>
              )}
            </>
          )}

          {provider === 'hidemium' && (
            <p>
                <Text size="sm">
                    For better results, it's recommended to use our model instead.
                </Text>
                <Text size="sm">
                    Hidemium uses the free Gemini model, which comes with some limitations.
                </Text>
            </p>
          )}

          <Group position="right" mt="md">
            <Button variant={isTet ? 'outline' : 'outline'} onClick={() => setOpened(false)}>Cancel</Button>
            <Button variant={isTet ? 'outline' : 'outline'} onClick={handleSave}>Save</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default ConfigModel;